// src/utils/simple-downloader.js
import axios from 'axios';

export class SimpleDownloader {
    constructor(options) {
        options = options || {};
        this.maxRetries = options.maxRetries || 3;
        this.retryDelay = options.retryDelay || 1000;
        this.timeout = options.timeout || 30000;
    }

    /**
     * 下载文件
     * @param {string} url 下载URL
     * @param {string} filename 文件名
     * @param {Function} onProgress 进度回调
     * @returns {Promise<boolean>} 下载是否成功
     */
    async downloadFile(url, filename, onProgress) {
        onProgress = onProgress || null;
        
        // 验证参数
        if (!url || !filename) {
            throw new Error('URL and filename are required');
        }

        // 清理URL
        var cleanUrl = this.cleanUrl(url);
        
        for (var attempt = 1; attempt <= this.maxRetries; attempt++) {
            try {
                console.log('Download attempt ' + attempt + '/' + this.maxRetries + ' for ' + filename);
                
                // 先检查文件是否存在
                await this.checkFileExists(cleanUrl);
                
                // 尝试下载
                var success = await this.attemptDownload(cleanUrl, filename, onProgress);
                if (success) {
                    console.log('Download successful for ' + filename);
                    return true;
                }
            } catch (error) {
                console.warn('Download attempt ' + attempt + ' failed for ' + filename + ':', error.message);
                
                if (attempt === this.maxRetries) {
                    throw new Error('Download failed after ' + this.maxRetries + ' attempts: ' + error.message);
                }
                
                // 指数退避延迟
                var delay = this.retryDelay * Math.pow(2, attempt - 1);
                console.log('Retrying download in ' + delay + 'ms...');
                await this.sleep(delay);
            }
        }
        
        return false;
    }

    /**
     * 检查文件是否存在
     * @param {string} url 
     */
    async checkFileExists(url) {
        try {
            var response = await axios.head(url, {
                timeout: 10000,
                validateStatus: function(status) {
                    return status < 500;
                }
            });
            
            if (response.status === 404) {
                throw new Error('File not found (404)');
            }
            
            if (response.status >= 400) {
                throw new Error('File check failed with status ' + response.status);
            }
            
            return true;
        } catch (error) {
            if (error.code === 'ECONNABORTED') {
                throw new Error('File check timeout');
            }
            throw error;
        }
    }

    /**
     * 尝试下载文件
     * @param {string} url 
     * @param {string} filename 
     * @param {Function} onProgress 
     */
    async attemptDownload(url, filename, onProgress) {
        try {
            var self = this;
            var response = await axios.get(url, {
                responseType: 'blob',
                timeout: this.timeout,
                onDownloadProgress: function(progressEvent) {
                    if (onProgress && progressEvent.total) {
                        var percent = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                        onProgress(percent);
                    }
                },
                headers: {
                    'Cache-Control': 'no-cache',
                    'Pragma': 'no-cache'
                }
            });

            // 验证响应
            if (!response.data || response.data.size === 0) {
                throw new Error('Downloaded file is empty');
            }

            // 创建下载
            await this.createDownload(response.data, filename, response.headers['content-type']);
            return true;
            
        } catch (error) {
            if (error.code === 'ECONNABORTED') {
                throw new Error('Download timeout');
            }
            if (error.response && error.response.status === 404) {
                throw new Error('File not found');
            }
            if (error.response && error.response.status >= 500) {
                throw new Error('Server error: ' + error.response.status);
            }
            throw error;
        }
    }

    /**
     * 创建下载链接并触发下载
     * @param {Blob} blob 
     * @param {string} filename 
     * @param {string} contentType 
     */
    async createDownload(blob, filename, contentType) {
        // 验证blob
        if (!(blob instanceof Blob)) {
            throw new Error('Invalid blob data');
        }

        // 确保正确的MIME类型
        var mimeType = contentType || this.getMimeType(filename);
        var correctedBlob = new Blob([blob], { type: mimeType });

        // 创建下载链接
        var downloadUrl = window.URL.createObjectURL(correctedBlob);
        
        try {
            var downloadLink = document.createElement("a");
            downloadLink.href = downloadUrl;
            downloadLink.download = filename;
            downloadLink.style.display = 'none';
            
            // 添加到DOM并触发下载
            document.body.appendChild(downloadLink);
            downloadLink.click();
            
            // 清理
            document.body.removeChild(downloadLink);
            
            // 延迟清理URL对象，确保下载开始
            setTimeout(function() {
                window.URL.revokeObjectURL(downloadUrl);
            }, 1000);
            
        } catch (error) {
            // 确保清理URL对象
            window.URL.revokeObjectURL(downloadUrl);
            throw error;
        }
    }

    /**
     * 清理URL，避免常见问题
     * @param {string} url 
     */
    cleanUrl(url) {
        if (!url) return '';
        
        // 移除多余的斜杠
        var cleanUrl = url.replace(/([^:]\/)\/+/g, '$1');
        
        // 确保协议正确
        if (!cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://')) {
            cleanUrl = 'https://' + cleanUrl;
        }
        
        return cleanUrl;
    }

    /**
     * 根据文件扩展名获取MIME类型
     * @param {string} filename 
     */
    getMimeType(filename) {
        var ext = filename.split('.').pop().toLowerCase();
        var mimeTypes = {
            'jpg': 'image/jpeg',
            'jpeg': 'image/jpeg',
            'png': 'image/png',
            'webp': 'image/webp',
            'gif': 'image/gif',
            'mp4': 'video/mp4',
            'avi': 'video/avi',
            'mov': 'video/quicktime',
            'wmv': 'video/x-ms-wmv'
        };
        
        return mimeTypes[ext] || 'application/octet-stream';
    }

    /**
     * 延迟函数
     * @param {number} ms 
     */
    sleep(ms) {
        return new Promise(function(resolve) {
            setTimeout(resolve, ms);
        });
    }
}